import { CloseButton } from "components/atoms/Icon";
import { Paragraph } from "components/atoms/Typography";
import { DataCoverageIcon } from "components/molecules/DataCoverage/DataCoverageIcon";
import posthog from "posthog-js";
import useFilters from "recoilStore/useFilters";
import { uniqueEnergyTypes } from "utils/constants";
import { getDisplayInfo } from "utils/formatting";
import { filterConditionToDisplayValueMap } from "../FilterSidebar.constants";
import "./index.css";

export function FilterLabelCards() {
    const { filters, removeFilter, removeFilters, numberOfFilters } = useFilters();
    const { stringFilters, numericFilters, dateFilters, dataCoverageFilter } = filters;

    const removeFiltersHandler = async () => {
        await Promise.resolve(removeFilters());
    };

    const combinedFilters = [...stringFilters, ...numericFilters, ...dateFilters];

    if (numberOfFilters === 0) return null;
    return (
        <div className="filter-name-cards">
            {dataCoverageFilter.length > 0 && (
                <DataCoverageLabelCard filter={dataCoverageFilter} onRemove={() => removeFilter("data_coverage")} />
            )}
            {combinedFilters.map((filter) => {
                const { field } = filter;

                const handleRemove = async () => {
                    posthog.capture("Filter Removed");
                    await Promise.resolve(removeFilter(field));

                    removeFilter(field);
                };

                return <FilterLabelCard key={field} filter={filter} onRemove={handleRemove} />;
            })}
            <button
                style={{
                    color: "var(--audette-gray-600)",
                    marginLeft: "auto",
                }}
                type="button"
                onClick={removeFiltersHandler}
            >
                <Paragraph>Clear filters</Paragraph>
            </button>
        </div>
    );
}

interface FilterLabelCardProps {
    filter: StringFilter | NumericFilter | DateFilter;
    onRemove: () => void;
}

function FilterLabelCard({ filter, onRemove }: FilterLabelCardProps) {
    const label = buildLabel(filter);

    return (
        <div className="filter-label-card">
            <Paragraph size="small">{label}</Paragraph>
            {/* Commenting this until the onRemove work */}
            {/* <CloseButton onClick={onRemove} />  */}
        </div>
    );
}

const buildLabel = (filter: StringFilter | NumericFilter | DateFilter) => {
    const { key, value, condition } = filter;
    const { humanReadable, formatFunction } = getDisplayInfo(key);

    if (key === "tags") {
        // don't format tags
        return `${humanReadable}: ${value.join(", ")}`;
    }

    const formattedValue: (string | number)[] = [];
    if (key === "energyType") {
        // Do not include inferred energy type values
        value.forEach((item) => {
            if (uniqueEnergyTypes.includes(item.toString())) formattedValue.push(formatFunction(item));
        });
    } else {
        value.forEach((v) => formattedValue.push(formatFunction(v)));
    }

    if (condition === "between") {
        return `${humanReadable}: is between ${formattedValue[0]} and ${formattedValue[1]}`;
    }
    const formattedCondition = filterConditionToDisplayValueMap[condition].toLowerCase();

    if (typeof formattedValue[0] === "string")
        return `${humanReadable}: ${formattedCondition} ${formattedValue.join(", ")}`;

    return <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>{formattedValue.map((value) => value)}</div>;
};

interface DataCoverageLabelCardProps {
    filter: DataCoverageEnum[];
    onRemove: () => void;
}

function DataCoverageLabelCard({ filter, onRemove }: DataCoverageLabelCardProps) {
    const warningItems: DataCoverageEnum[] = ["equipment_warning", "utility_warning", "equipment_and_utility_warning"];

    const hasWarningItems = warningItems.some((item) => filter.includes(item));
    const nonWarningItems = filter.filter((item) => !warningItems.includes(item));

    const displayItems = [...nonWarningItems, ...(hasWarningItems ? ["equipment_warning" as DataCoverageEnum] : [])];

    return (
        <div className="filter-label-card">
            {displayItems.map((identifier, index) => (
                <DataCoverageIcon
                    key={hasWarningItems && identifier === "equipment_warning" ? "warning_group" : identifier}
                    identifier={identifier}
                />
            ))}
            <CloseButton onClick={onRemove} />
        </div>
    );
}
